import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import {
  shouldUseRestingComposerLayout,
  shouldUseMobileComposerLayout,
} from "../composerFooterLayout";
import {
  createComposerScrollCollapseState,
  suppressComposerScrollCollapse,
  type ComposerScrollCollapseState,
} from "./composerScrollCollapse";
import { useIsMobile } from "~/hooks/useMediaQuery";
import { useComposerFocusState } from "./useComposerFocusState";
import { useComposerFocusTracking, type ComposerFocusHandlers } from "./useComposerFocusTracking";
import { useComposerScrollCollapse } from "./useComposerScrollCollapse";

export interface RestingComposerDecisionInput {
  /**
   * False only on the new-thread landing. A local draft thread is still a
   * thread route with a timeline to read back through, so it rests; the
   * landing has no history and every control has to stay visible.
   */
  readonly isExistingThread: boolean;
  /** Banners, approvals, pending questions and validation all hold it open. */
  readonly hasExpandedChrome: boolean;
  readonly collapseOnBlur: boolean;
  readonly hasMultilinePrompt?: boolean;
  /** Changes when the person moves to another thread or draft. */
  readonly threadKey: string;
  readonly getTimelineScrollNode: () => HTMLElement | null;
  readonly isTimelineAtEnd: () => boolean;
  /** Put the caret back in the editor after a press on the Composer itself. */
  readonly focusEditor: () => void;
}

/**
 * Whether the Composer is resting, and everything that changes the answer.
 *
 * Separate from the layout half so this file holds exactly one question. The
 * triggers are a wheel gesture, focus arriving and leaving, typing, a press on
 * the Composer, a window coming back, a selection held in the timeline, the
 * viewport, the chrome above the prompt and one setting, and they all meet in
 * the pure `shouldUseRestingComposerLayout`.
 */
export function useRestingComposerDecision(input: RestingComposerDecisionInput): {
  readonly isResting: boolean;
  /**
   * Below `md`, from the one media query the Composer already subscribes to.
   * Reported rather than kept private because the footer needs the same answer
   * to decide whether it owns the task anchor and the plan sidebar toggle, and
   * a second `useIsMobile()` call in the orchestrator would be a second place
   * for the breakpoint to drift.
   */
  readonly isMobileViewport: boolean;
  readonly restoreAfterTimelineReachedEnd: () => void;
} {
  const {
    isExistingThread,
    hasExpandedChrome,
    collapseOnBlur,
    threadKey,
    getTimelineScrollNode,
    isTimelineAtEnd,
    focusEditor,
  } = input;

  const isMobileViewport = useIsMobile();
  // One statement of "this Composer is somewhere the Resting layout applies",
  // read by the gesture, the eligibility ref and the reset. Spelling it out
  // three times is three places for them to disagree.
  const focus = useComposerFocusState(isMobileViewport);
  const { setIsComposerScrollCollapsed, restoreAfterTimelineReachedEnd } = focus;

  // The server knows neither where the timeline sits nor whether the editor has
  // focus, and an unfocused Composer is a resting one. Guessing would put the
  // Resting Composer in the HTML of every thread page and then expand it on
  // hydration, which is a collapse nobody asked for on every page load.
  const hasMounted = useHasMounted();

  const restingInput = {
    isExistingThread,
    isMobileViewport,
    isFocused: focus.isComposerFocused,
    isScrollCollapsed: focus.isComposerScrollCollapsed,
    hasExpandedChrome,
    collapseOnBlur,
    hasMultilinePrompt: input.hasMultilinePrompt,
  };
  const canRest = shouldUseRestingComposerLayout({ ...restingInput, isScrollCollapsed: true });
  const desktopResting = hasMounted && shouldUseRestingComposerLayout(restingInput);

  const isResting =
    desktopResting ||
    shouldUseMobileComposerLayout({
      isMobileViewport,
      isFocused: focus.isComposerFocused,
      hasExpandedChrome,
      hasMultilinePrompt: input.hasMultilinePrompt,
    });

  // Read at event time rather than closed over: the wheel listener is attached
  // once per thread and the answer changes on every render. Not gated on the
  // blur preference, because a scroll collapse is something the person asked
  // for with a gesture and it lifts on the next Composer interaction.
  const eligibleRef = useRef(false);
  const restingRef = useRef(false);
  restingRef.current = isResting;

  const gestureRef = useRef(createComposerScrollCollapseState());
  useComposerFocusTracking({
    ...useComposerRestingFocusHandlers(focus, gestureRef, focusEditor),
    getTimelineScrollNode,
    isRestingRef: restingRef,
  });

  const onScrollCollapse = useCallback(() => {
    setIsComposerScrollCollapsed(true);
  }, [setIsComposerScrollCollapsed]);

  eligibleRef.current = canRest && !isResting;

  useComposerScrollCollapse({
    enabled: isExistingThread && !isMobileViewport,
    eligibleRef,
    gestureRef,
    getTimelineScrollNode,
    isTimelineAtEnd,
    onScrollCollapse,
    onTimelineReachedEnd: restoreAfterTimelineReachedEnd,
  });

  useRestingComposerReset({
    threadKey,
    canRest,
    restoreAfterTimelineReachedEnd,
    setIsComposerScrollCollapsed,
  });

  return { isResting, isMobileViewport, restoreAfterTimelineReachedEnd };
}

function useHasMounted(): boolean {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}

/**
 * What focus, typing and a press on the Composer mean for the Resting layout.
 *
 * Focus is the strongest possible statement that the person wants to type, so
 * it lifts a scroll collapse as well rather than leaving the Composer resting
 * under a cursor. Typing says the same thing and also retires the rest of the
 * wheel gesture, whose momentum tail would otherwise rest the Composer again a
 * frame after the first keystroke expanded it.
 */
function useComposerRestingFocusHandlers(
  focus: ReturnType<typeof useComposerFocusState>,
  gestureRef: RefObject<ComposerScrollCollapseState>,
  focusEditor: () => void,
): ComposerFocusHandlers {
  const { setIsComposerFocused, setIsComposerScrollCollapsed } = focus;
  const onFocused = useCallback(
    (options: { readonly liftScrollCollapse: boolean }) => {
      setIsComposerFocused(true);
      if (options.liftScrollCollapse) setIsComposerScrollCollapsed(false);
    },
    [setIsComposerFocused, setIsComposerScrollCollapsed],
  );
  const onBlurred = useCallback(() => {
    setIsComposerFocused(false);
  }, [setIsComposerFocused]);
  const onEditorActivity = useCallback(() => {
    suppressComposerScrollCollapse(gestureRef.current, performance.now());
    setIsComposerScrollCollapsed(false);
  }, [gestureRef, setIsComposerScrollCollapsed]);
  const onSurfacePressed = useCallback(
    (options: { readonly restingPadding: boolean }) => {
      setIsComposerScrollCollapsed(false);
      if (!options.restingPadding) return;
      setIsComposerFocused(true);
      focusEditor();
    },
    [focusEditor, setIsComposerFocused, setIsComposerScrollCollapsed],
  );
  return { onFocused, onBlurred, onEditorActivity, onSurfacePressed };
}

/**
 * The two things that put the Composer back to Expanded without being asked.
 */
function useRestingComposerReset(input: {
  readonly threadKey: string;
  readonly canRest: boolean;
  readonly restoreAfterTimelineReachedEnd: () => void;
  readonly setIsComposerScrollCollapsed: (collapsed: boolean) => void;
}): void {
  const { threadKey, canRest, restoreAfterTimelineReachedEnd, setIsComposerScrollCollapsed } =
    input;

  // Opening a thread lands on its newest turn, which is the Expanded state.
  // Nothing announces that: the timeline only reports a change, and it is
  // already at its end by the time it mounts.
  useEffect(() => {
    restoreAfterTimelineReachedEnd();
  }, [threadKey, restoreAfterTimelineReachedEnd]);

  // Chrome appearing, or the window crossing to a phone width, must not leave
  // a collapse behind that reasserts itself the moment the chrome clears.
  useEffect(() => {
    if (canRest) return;
    setIsComposerScrollCollapsed(false);
  }, [canRest, setIsComposerScrollCollapsed]);
}
